import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import { DropdownTemplate } from "@ryde/controls";
import { useSelectedIds } from "@ryde/redux";
import {
  enumToSelectList,
  FilterStationFormFieldsEnum,
  ISelectListItem,
  isEqualNumberArraysAnyOrder,
  PassengerMapStationFilterTypeEnum,
  passengersStationsMapLastFilterSearchAtom,
} from "@ryde/shared";

import { usePassengerMapFilterStationPanel } from "../../hooks";
import { PassengersFilterStationsPanelStyled } from "./passengers.map.styled";

const PassengerMapStationFilterSelectedList: ISelectListItem[] =
  enumToSelectList(PassengerMapStationFilterTypeEnum);

interface IPassengersMapFilterStationsPanelProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const PassengersMapFilterStationsPanel: FC<
  IPassengersMapFilterStationsPanelProps
> = ({ isOpen, handleClose }) => {
  const { selectedRowsIds } = useSelectedIds();
  const { methods, handleChange } = usePassengerMapFilterStationPanel();

  const stationsMapLastFilterSearch = useRecoilValue(
    passengersStationsMapLastFilterSearchAtom,
  );

  const { setValue, watch } = methods;
  const filterTypeWatched = watch(FilterStationFormFieldsEnum.filterType);
  const { t } = useTranslation();

  useEffect(() => {
    if (stationsMapLastFilterSearch.filterType && isOpen) {
      setValue(
        FilterStationFormFieldsEnum.filterType,
        stationsMapLastFilterSearch.filterType,
        { shouldDirty: true, shouldValidate: true },
      );
    }
  }, [stationsMapLastFilterSearch, setValue, isOpen]);

  useEffect(() => {
    const last = stationsMapLastFilterSearch;

    const idsChanged = !isEqualNumberArraysAnyOrder(
      selectedRowsIds,
      last?.selectedPassengersIds ?? [],
    );
    const viewChanged =
      isOpen && filterTypeWatched && filterTypeWatched !== last?.filterType;

    const shouldFetchAgain = idsChanged || viewChanged;

    if (shouldFetchAgain) {
      handleChange();
    }
  }, [
    isOpen,
    selectedRowsIds,
    filterTypeWatched,
    stationsMapLastFilterSearch,
    handleChange,
  ]);

  return (
    isOpen && (
      <FormProvider {...methods}>
        <PassengersFilterStationsPanelStyled>
          <div className="dropdownInput">
            <DropdownTemplate
              name={FilterStationFormFieldsEnum.filterType}
              labelTranslated={t("general.filterPickupStationBy")}
              formAttributes={{
                selectItems: PassengerMapStationFilterSelectedList,
              }}
            />
          </div>

          <div className="actionsButtons">
            <PIC.XSVG className="close-icon-svg" onClick={handleClose} />
          </div>
        </PassengersFilterStationsPanelStyled>
      </FormProvider>
    )
  );
};
